/**
 * Reusable diff table for comparing two snapshots side-by-side.
 *
 * @module config-comparison/DiffTable
 */
import React from 'react';
import { Text, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import { type DiffRow, type DiffStatus, formatValue } from './diff-utils';

const diffMeta: Record<DiffStatus, { bg: string; label: string; badgeVariant: 'warning' | 'success' | 'destructive' }> = {
  changed: { bg: 'bg-warning/10', label: 'Changed', badgeVariant: 'warning' },
  added: { bg: 'bg-success/10', label: 'Added', badgeVariant: 'success' },
  removed: { bg: 'bg-destructive/10', label: 'Removed', badgeVariant: 'destructive' },
  unchanged: { bg: '', label: '', badgeVariant: 'warning' },
};

function DiffRowComponent({ row }: { row: DiffRow }) {
  const meta = diffMeta[row.status];
  const dimmed = row.status === 'unchanged';
  const cellClass = `px-3 py-1.5 font-mono text-xs${dimmed ? ' text-muted-foreground' : ''}`;

  return (
    <tr className={`border-b ${meta.bg}`}>
      <td className={cellClass}>{row.key}</td>
      <td className={cellClass}>{formatValue(row.valueA)}</td>
      <td className={cellClass}>{formatValue(row.valueB)}</td>
      <td className="px-3 py-1.5">
        {meta.label && <TagBadge name={meta.label} color={badgeVariantToTagColor(meta.badgeVariant)} hideDot className="text-xs" />}
      </td>
    </tr>
  );
}

interface DiffTableProps {
  rows: DiffRow[];
  labelA: string;
  labelB: string;
}

export function DiffTable({ rows, labelA, labelB }: DiffTableProps) {
  const [showUnchanged, setShowUnchanged] = React.useState(false);
  const changedRows = rows.filter((r) => r.status !== 'unchanged');
  const unchangedRows = rows.filter((r) => r.status === 'unchanged');

  if (rows.length === 0) {
    return <Text className="text-muted-foreground text-sm">No data available.</Text>;
  }

  return (
    <div className="border rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left px-3 py-2 font-medium text-muted-foreground">Key</th>
            <th className="text-left px-3 py-2 font-medium text-muted-foreground">{labelA}</th>
            <th className="text-left px-3 py-2 font-medium text-muted-foreground">{labelB}</th>
            <th className="text-left px-3 py-2 font-medium text-muted-foreground w-20"></th>
          </tr>
        </thead>
        <tbody>
          {changedRows.map((row) => <DiffRowComponent key={row.key} row={row} />)}
          {unchangedRows.length > 0 && (
            <tr className="border-b bg-muted/30">
              <td colSpan={4} className="px-3 py-1.5">
                <button
                  onClick={() => setShowUnchanged(!showUnchanged)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  {showUnchanged ? '▾' : '▸'} {unchangedRows.length} unchanged {unchangedRows.length === 1 ? 'value' : 'values'}
                </button>
              </td>
            </tr>
          )}
          {showUnchanged && unchangedRows.map((row) => <DiffRowComponent key={row.key} row={row} />)}
        </tbody>
      </table>
    </div>
  );
}
